/**
 * ============================================================================
 * CALENDAR.JS v35.4 - FAB POSITION FIX (Button Strategy)
 * ============================================================================
 *
 * ✅ v35.4: FAB POSITION FIX
 *           - FAB "Neuer Termin" aus cal-header-row2-controls ENTFERNT
 *           - FAB jetzt AUSSERHALB von cal-body (overflow-Schutz)
 *           - Modal-Guard: FAB hidden wenn Modal/Import/TemplateManager offen
 *           - Row2 ist jetzt nur noch Kategorie-Filter (volle Breite)
 *
 * ✅ v35.3: NOTIFICATION BANNER FIX
 *           - showNotificationBanner wird NICHT mehr auf true gesetzt
 *           - Beide Banner (Notification + Reminder) bleiben aus
 *           - Alles läuft automatisch über initReminders()
 *
 * ✅ v35.2: AUTO NOTIFICATION REQUEST
 *           - Benachrichtigungen werden AUTOMATISCH aktiviert
 *           - Kein Banner mehr - Permission wird direkt angefragt
 *           - 1.5s Verzögerung für bessere UX
 *           - Toast-Feedback bei Erfolg
 *
 * ✅ v35.1: REMINDER BANNER FIX
 *           - ReminderService v1.1 mit Capacitor LocalNotifications
 *
 * ✅ v35.0: HEADER REDESIGN
 *           - Row 1: Icon + Title "Kalender" + Info Button + Hamburger
 *           - Row 2: Kategorie Filter + Neuer Termin Button
 *           - Row 3: Navigation (◁ ▷) + Heute + View Selector
 *           - Info Modal mit Modul-Beschreibung
 *
 * ✅ v34.0: SUB-VIEW INTEGRATION
 *           - WeekView v7.4 → v8.0 (CSS class-based, full production logic)
 *           - MonthView v3.0 → v4.0 (CSS class-based)
 *           - DayView v4.0 onTimeSlotClick fix (date + time args)
 *           - DayView v4.0 onAppointmentDrop fix (id, date, time args)
 *           - Calendar.css v3.1 mit 2935 Zeilen (all 4 views)
 *
 * ✅ v33.0: PLANIVIO CSS REDESIGN
 *           - ALL inline styles → CSS classes (Calendar.css v3.0)
 *           - Removed getColors() helper
 *           - Removed containerStyles useMemo
 *           - cal- prefix namespace on all classes
 *           - Dark mode via .dark class on root
 *           - Mobile via .cal-mobile class on root
 *           - Business logic 100% unchanged from v32.5
 *
 * ✅ v32.5: VIEW SELECTOR FIX
 * ✅ v32.4: NEU-BUTTON IN NAVIGATION
 * ✅ v32.3: NAVIGATION MIT KW-ANZEIGE
 * ✅ v32.2: HAMBURGER POSITION FIX
 * ✅ v32.1: MOBILE EXPORT MENU (Hamburger)
 * ✅ v32.0: MOBILE DESIGN GUIDE
 * 🔧 v31.0: PWA OFFLINE SUPPORT
 * 🔧 v30.0: BROWSER REMINDERS
 * 🔧 v29.0: CATEGORY FILTER
 * 🔧 v28.0: ICS IMPORT
 * 🔧 v27.0: PDF EXPORT
 * 🔧 v26.0: VOLLTEXTSUCHE
 * 🔧 v25.2: TIMEZONE FIX
 * 🔧 v24.0: EVENT TEMPLATES
 * 🔧 v23.0: TOKEN CONTEXT
 *
 * @version 35.4.0 - FAB Position Fix (Button Strategy)
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useToken } from '../../context/TokenContext';
import { useOffline } from '../../context/OfflineContext';
import { indexedDBService } from '../../services/indexedDB.service';
import { useTranslation } from '../../context/I18nContext';
import { useTheme } from '../../context/ThemeContext';
import Loading, { 
  LoadingIndicator, 
  Skeleton, 
  SkeletonGrid 
} from '../common/Loading';
import notificationService from '../../services/notification.service';
import icsService from '../../services/ics.service';
import AppointmentModal from './AppointmentModal';
import DayView from './DayView';
import WeekView from './WeekView';
import MonthView from './MonthView';
import AgendaView from './AgendaView';
import TemplateSelector from './TemplateSelector';
import TemplateManager from './TemplateManager';
import { exportMonthToPDF, exportWeekToPDF, exportDayToPDF } from './exportToPDF';
import {
  formatInputDate,
  generateRecurringAppointments
} from './calendarUtils';
import {
  initReminderService,
  scheduleRemindersForAppointments,
  getNotificationPermission,
  requestNotificationPermission,
  cancelAllReminders
} from './ReminderService';
import {
  isCalendarCacheLoaded,
  setCalendarCacheLoaded,
  subscribeToInvalidation
} from './calendarCache';
import './Calendar.css';

// ============================================================================
// ANALYTICS TRACKING
// ============================================================================
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const LOADING_DELAY_MS = 200;

console.log('📅 Calendar.js v35.4 loaded - FAB POSITION FIX 🎯');

// Category Configuration
const CATEGORIES = [
  { value: 'privat', label: 'Privat', icon: '🏠', color: '#4A90A4' },
  { value: 'arbeit', label: 'Arbeit', icon: '💼', color: '#8B5CF6' },
  { value: 'arzt', label: 'Arzt', icon: '🏥', color: '#EF4444' },
  { value: 'schule', label: 'Schule', icon: '🎓', color: '#22C55E' },
  { value: 'sport', label: 'Sport', icon: '⚽', color: '#06B6D4' },
  { value: 'familie', label: 'Familie', icon: '👨‍👩‍👧‍👦', color: '#EC4899' },
  { value: 'sonstiges', label: 'Sonstiges', icon: '📌', color: '#6B7280' }
];

/**
 * Safe Track Function
 */
const trackAnalyticsEvent = async (token, eventName, metadata = {}) => {
  if (!token || !eventName) return;
  
  try {
    const event = {
      type: 'event',
      event_name: String(eventName).substring(0, 100),
      component: String(metadata.component || 'Calendar').substring(0, 100),
      metadata: metadata,
      timestamp: new Date().toISOString()
    };
    
    const response = await fetch(`${API_BASE_URL}/api/analytics/track/batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ events: [event] })
    });
    
    if (response.ok) {
      console.log(`✅ Analytics tracked: ${eventName}`, metadata);
    } else {
      console.warn(`❌ Analytics failed: ${response.status}`, eventName);
    }
  } catch (err) {
    console.warn(`❌ Analytics error: ${eventName}`, err.message);
  }
};

// ============================================================================
// GLOBALER STATE + SHARED PROMISE
// ============================================================================
let GLOBAL_APPOINTMENTS_CACHE = [];
let GLOBAL_HAS_LOADED = false;
let GLOBAL_LOADING_PROMISE = null;

const checkCacheValid = () => {
  const externalCacheValid = isCalendarCacheLoaded();

  if (!externalCacheValid && GLOBAL_HAS_LOADED) {
    console.log('📅 External cache invalidation detected, clearing local cache');
    GLOBAL_HAS_LOADED = false;
    GLOBAL_APPOINTMENTS_CACHE = [];
  }

  return GLOBAL_HAS_LOADED && externalCacheValid;
};

// ============================================================================
// PLANIVIO STORAGE KEYS
// ============================================================================
const STORAGE_KEY_DATE = 'planivio_calendar_currentDate';
const STORAGE_KEY_VIEW = 'planivio_calendar_view';

const getSavedDate = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY_DATE);
    if (saved) {
      const date = new Date(saved);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }
  } catch (e) {}
  return new Date();
};

const getSavedView = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY_VIEW);
    if (saved && ['day', 'week', 'month', 'agenda'].includes(saved)) {
      return saved;
    }
  } catch (e) {}
  return 'week';
};

// ============================================================================
// INLINE SWIPE HOOK
// ============================================================================
const useSwipe = (options = {}) => {
  const {
    onSwipeLeft = () => {},
    onSwipeRight = () => {},
    minSwipeDistance = 50,
    maxSwipeTime = 500
  } = options;

  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const touchStartTime = useRef(null);

  const handleTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    touchStartX.current = touch.clientX;
    touchStartY.current = touch.clientY;
    touchStartTime.current = Date.now();
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (touchStartX.current === null) return;
    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStartX.current;
    const deltaY = touch.clientY - touchStartY.current;
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
      e.preventDefault();
    }
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (touchStartX.current === null) return;
    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - touchStartX.current;
    const deltaY = touch.clientY - touchStartY.current;
    const deltaTime = Date.now() - touchStartTime.current;
    const isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY);
    const isLongEnough = Math.abs(deltaX) >= minSwipeDistance;
    const isFastEnough = deltaTime <= maxSwipeTime;
    if (isHorizontalSwipe && isLongEnough && isFastEnough) {
      if (deltaX > 0) {
        onSwipeRight();
      } else {
        onSwipeLeft();
      }
    }
    touchStartX.current = null;
    touchStartY.current = null;
    touchStartTime.current = null;
  }, [minSwipeDistance, maxSwipeTime, onSwipeLeft, onSwipeRight]);

  return {
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd
  };
};

// ============================================================================
// API SERVICE
// ============================================================================
const calendarAPI = {
  async sync(token) {
    const response = await fetch(`${API_BASE_URL}/api/calendar`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });
    return await response.json();
  },
  
  async save(data, token) {
    const response = await fetch(`${API_BASE_URL}/api/calendar`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(data)
    });
    return await response.json();
  },
  
  async update(data, token) {
    const response = await fetch(`${API_BASE_URL}/api/calendar/${data.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(data)
    });
    return await response.json();
  },
  
  async delete(data, token) {
    const response = await fetch(`${API_BASE_URL}/api/calendar/${data.id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });
    return await response.json();
  }
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
function Calendar({ active }) {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const { token, loading: tokenLoading } = useToken();
  const { isOnline } = useOffline();
  const { t } = useTranslation();
  const { isDark } = useTheme();

  const VIEW_CONFIG = [
    { id: 'day', icon: '📄', label: t('calendar.views.day') },
    { id: 'week', icon: '📅', label: t('calendar.views.week') },
    { id: 'month', icon: '📆', label: t('calendar.views.month') },
    { id: 'agenda', icon: '📋', label: t('calendar.views.agenda') }
  ];

  // ============================================================================
  // STATE
  // ============================================================================
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const [appointments, setAppointments] = useState(() => GLOBAL_APPOINTMENTS_CACHE);
  const [currentDate, setCurrentDate] = useState(getSavedDate);
  const [view, setView] = useState(getSavedView);
  const [isLoading, setIsLoading] = useState(() => !checkCacheValid());
  const [showLoading, setShowLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(() => !checkCacheValid());
  const [error, setError] = useState(null);
  
  const [showModal, setShowModal] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [showNotificationBanner, setShowNotificationBanner] = useState(false);

  const [swipeDirection, setSwipeDirection] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [showTemplateManager, setShowTemplateManager] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchTimeoutRef = useRef(null);

  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState([]);
  const [importError, setImportError] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef(null);
  const hamburgerRef = useRef(null);

  const [selectedCategories, setSelectedCategories] = useState(() => {
    try {
      const saved = localStorage.getItem('planivio_calendar_categories');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch (e) {}
    return [];
  });
  const [showCategoryFilter, setShowCategoryFilter] = useState(false);

  const [showMobileExportMenu, setShowMobileExportMenu] = useState(false);
  const [showInfo, setShowInfo] = useState(false);

  const [reminderPermission, setReminderPermission] = useState('default');
  const [showReminderBanner, setShowReminderBanner] = useState(false);

  // ============================================================================
  // REFS
  // ============================================================================
  const loadingTimerRef = useRef(null);
  const isMountedRef = useRef(true);
  const hasTrackedViewRef = useRef(false);

  // ============================================================================
  // ROOT CLASS NAME
  // ============================================================================
  const rootClassName = [
    'calendar-redesign',
    isDark ? 'dark' : '',
    isMobile ? 'cal-mobile' : ''
  ].filter(Boolean).join(' ');

  // ============================================================================
  // EFFECTS
  // ============================================================================
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // FAB Event
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.module === 'calendar') {
        setEditingAppointment(null);
        setShowModal(true);
      }
    };
    window.addEventListener('planivio-create-new', handler);
    return () => window.removeEventListener('planivio-create-new', handler);
  }, []);

  useEffect(() => {
    if (!showMobileExportMenu && !showCategoryFilter) return;

    const handleClickOutside = (e) => {
      if (showMobileExportMenu && !e.target.closest('.cal-hamburger-wrapper')) {
        setShowMobileExportMenu(false);
      }
      if (showCategoryFilter && !e.target.closest('.cal-filter-wrapper')) {
        setShowCategoryFilter(false);
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showMobileExportMenu, showCategoryFilter]);

  useEffect(() => {
    isMountedRef.current = true;
    console.log('🔄 Calendar MOUNTED');

    return () => {
      isMountedRef.current = false;
      console.log('🔄 Calendar UNMOUNTING');
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
      }
      cancelAllReminders();
    };
  }, []);

  useEffect(() => {
    const initReminders = async () => {
      const result = await initReminderService();
      console.log('🔔 ReminderService initialized:', result);
      setReminderPermission(result.permission);

      if (result.permission === 'default' || result.permission === 'prompt') {
        console.log('🔔 Auto-requesting notification permission...');

        setTimeout(async () => {
          const permission = await requestNotificationPermission();
          console.log('🔔 Auto-request result:', permission);
          setReminderPermission(permission);

          if (permission === 'granted') {
            notificationService.success('🔔 Erinnerungen aktiviert!');
          }
        }, 1500);
      } else if (result.permission === 'granted') {
        console.log('🔔 Permission already granted');
      } else if (result.permission === 'denied') {
        console.log('🔔 Notifications blocked - user needs to enable in settings');
      }

      setShowReminderBanner(false);
    };

    initReminders();
  }, []);

  useEffect(() => {
    if (token && !hasTrackedViewRef.current && active) {
      hasTrackedViewRef.current = true;
      console.log('🔐 Calendar: Using token from TokenContext');
      trackAnalyticsEvent(token, 'component_view', { 
        component: 'Calendar',
        initialView: getSavedView()
      });
    }
  }, [token, active]);

  const trackEvent = useCallback(async (eventName, metadata = {}) => {
    if (!token) {
      console.warn('❌ No token available for analytics');
      return;
    }
    
    trackAnalyticsEvent(token, eventName, {
      component: 'Calendar',
      ...metadata
    });
  }, [token]);

  useEffect(() => {
    if (appointments.length > 0 && reminderPermission === 'granted') {
      const count = scheduleRemindersForAppointments(appointments);
      console.log(`🔔 Scheduled ${count} reminders for ${appointments.length} appointments`);
    }
  }, [appointments, reminderPermission]);

  const handleRequestReminderPermission = useCallback(async () => {
    const permission = await requestNotificationPermission();
    setReminderPermission(permission);
    setShowReminderBanner(false);

    if (permission === 'granted') {
      scheduleRemindersForAppointments(appointments);
      notificationService.success('Erinnerungen aktiviert!');
      trackEvent('reminder_permission_granted');
    } else if (permission === 'denied') {
      notificationService.warning('Erinnerungen wurden blockiert. Sie können dies in den Browser-Einstellungen ändern.');
      trackEvent('reminder_permission_denied');
    }
  }, [appointments, trackEvent]);

  const handleDismissReminderBanner = useCallback(() => {
    setShowReminderBanner(false);
    localStorage.setItem('planivio_reminder_banner_dismissed', 'true');
  }, []);

  useEffect(() => {
    if (appointments.length > 0) {
      GLOBAL_APPOINTMENTS_CACHE = appointments;
    }
  }, [appointments]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_DATE, currentDate.toISOString());
    } catch (e) {}
  }, [currentDate]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_VIEW, view);
    } catch (e) {}
  }, [view]);

  useEffect(() => {
    try {
      localStorage.setItem('planivio_calendar_categories', JSON.stringify(selectedCategories));
    } catch (e) {}
  }, [selectedCategories]);

  const filteredAppointments = useMemo(() => {
    if (selectedCategories.length === 0) {
      return appointments;
    }
    return appointments.filter(apt => {
      const category = apt.category || apt.type || 'privat';
      return selectedCategories.includes(category);
    });
  }, [appointments, selectedCategories]);

  const toggleCategoryFilter = useCallback((category) => {
    setSelectedCategories(prev => {
      if (prev.includes(category)) {
        return prev.filter(c => c !== category);
      } else {
        return [...prev, category];
      }
    });

    trackEvent('calendar_category_filter_toggle', {
      category,
      action: selectedCategories.includes(category) ? 'remove' : 'add'
    });
  }, [selectedCategories, trackEvent]);

  const clearCategoryFilter = useCallback(() => {
    setSelectedCategories([]);
    trackEvent('calendar_category_filter_clear');
  }, [trackEvent]);

  const selectAllCategories = useCallback(() => {
    setSelectedCategories(CATEGORIES.map(c => c.value));
    trackEvent('calendar_category_filter_select_all');
  }, [trackEvent]);

  const fetchAppointmentsFromBackend = useCallback(async () => {
    console.log('📡 Fetching appointments from backend...');
    
    if (!token) {
      throw new Error('Kein Token verfügbar');
    }
    
    const response = await calendarAPI.sync(token);

    if (response.success && response.appointments) {
      const completedApts = response.appointments.filter(a => a.isCompleted || a.is_completed);
      if (completedApts.length > 0) {
        console.log('📊 API returned', completedApts.length, 'completed appointments');
      }

      const allAppointments = generateRecurringAppointments(response.appointments);
      console.log('✅ Fetched', allAppointments.length, 'appointments');
      return allAppointments;
    }
    
    throw new Error(response.error || 'Failed to load appointments');
  }, [token]);

  const loadAppointments = useCallback(async (forceRefresh = false) => {
    if (!isOnline) {
      console.log('📴 Offline mode - loading from IndexedDB...');
      try {
        const cachedAppointments = await indexedDBService.getAppointments();
        if (cachedAppointments.length > 0) {
          console.log('📦 Loaded', cachedAppointments.length, 'appointments from IndexedDB (offline)');
          if (isMountedRef.current) {
            setAppointments(cachedAppointments);
            setIsLoading(false);
            setIsInitialLoad(false);
          }
          return;
        }
      } catch (err) {
        console.warn('⚠️ IndexedDB read failed:', err.message);
      }
      if (GLOBAL_APPOINTMENTS_CACHE.length > 0) {
        console.log('📦 Using in-memory cache (offline):', GLOBAL_APPOINTMENTS_CACHE.length, 'appointments');
        if (isMountedRef.current) {
          setAppointments(GLOBAL_APPOINTMENTS_CACHE);
          setIsLoading(false);
          setIsInitialLoad(false);
        }
        return;
      }
      if (isMountedRef.current) {
        setError('Offline - keine gecachten Termine verfügbar');
        setIsLoading(false);
        setIsInitialLoad(false);
      }
      return;
    }

    if (!token) {
      console.log('⏳ Waiting for token from TokenContext...');
      return;
    }

    if (checkCacheValid() && GLOBAL_APPOINTMENTS_CACHE.length > 0 && !forceRefresh) {
      console.log('📦 Using cached appointments:', GLOBAL_APPOINTMENTS_CACHE.length);
      if (isMountedRef.current) {
        setAppointments(GLOBAL_APPOINTMENTS_CACHE);
        setIsLoading(false);
        setIsInitialLoad(false);
      }
      return;
    }
    
    if (GLOBAL_LOADING_PROMISE) {
      console.log('⏳ Waiting for existing load...');
      try {
        const result = await GLOBAL_LOADING_PROMISE;
        console.log('✅ Got appointments from shared promise');
        if (isMountedRef.current) {
          setAppointments(result);
          setIsLoading(false);
          setIsInitialLoad(false);
        }
      } catch (err) {
        console.error('❌ Shared promise failed:', err);
        if (isMountedRef.current) {
          setError(err.message);
          setIsLoading(false);
          setIsInitialLoad(false);
        }
      }
      return;
    }
    
    console.log('🚀 Starting new load...');
    
    if (isMountedRef.current) {
      setIsLoading(true);
      setError(null);
      
      if (!isInitialLoad) {
        loadingTimerRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            setShowLoading(true);
          }
        }, LOADING_DELAY_MS);
      }
    }
    
    GLOBAL_LOADING_PROMISE = fetchAppointmentsFromBackend();
    
    try {
      const result = await GLOBAL_LOADING_PROMISE;

      GLOBAL_APPOINTMENTS_CACHE = result;
      GLOBAL_HAS_LOADED = true;
      setCalendarCacheLoaded(true);

      try {
        await indexedDBService.saveAppointments(result);
        console.log('💾 Appointments saved to IndexedDB');
      } catch (dbErr) {
        console.warn('⚠️ IndexedDB save failed:', dbErr.message);
      }

      if (isMountedRef.current) {
        setAppointments(result);
        setIsInitialLoad(false);
      }

    } catch (err) {
      console.error('❌ Load error:', err.message);
      
      trackEvent('calendar_load_error', { error: err.message });
      
      if (isMountedRef.current) {
        setError(err.message);
        setIsInitialLoad(false);
        
        if (GLOBAL_APPOINTMENTS_CACHE.length > 0) {
          setAppointments(GLOBAL_APPOINTMENTS_CACHE);
        }
      }
    } finally {
      GLOBAL_LOADING_PROMISE = null;
      
      if (isMountedRef.current) {
        setIsLoading(false);
        setShowLoading(false);
      }
      
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
    }
  }, [isInitialLoad, fetchAppointmentsFromBackend, trackEvent, token, isOnline]);

  useEffect(() => {
    if (!active) return;

    const handleExternalInvalidation = () => {
      console.log('📅 External cache invalidation received, reloading appointments...');
      GLOBAL_HAS_LOADED = false;
      if (token) {
        loadAppointments(true);
      }
    };

    const unsubscribe = subscribeToInvalidation(handleExternalInvalidation);

    return () => {
      unsubscribe();
    };
  }, [active, token, loadAppointments]);

  const loadTemplates = useCallback(async () => {
    if (!token) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/calendar/templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
        console.log('✅ Loaded', data.length, 'templates');
      }
    } catch (error) {
      console.error('❌ Failed to load templates:', error);
    }
  }, [token]);

  const applyTemplate = useCallback((template) => {
    setEditingAppointment({
      title: template.title || '',
      description: template.description || '',
      category: template.category || 'privat',
      person: template.person || 'alle',
      location: template.location || '',
      reminder_minutes: template.reminder_minutes,
      all_day: template.all_day || false,
      date: formatInputDate(currentDate),
      time: template.all_day ? '' : '09:00'
    });
    setShowModal(true);

    trackEvent('calendar_template_applied', {
      template_name: template.template_name,
      template_id: template.id
    });
  }, [currentDate, trackEvent]);

  const saveTemplate = useCallback(async (templateData, isUpdate = false) => {
    if (!token) {
      setError('Kein Token verfügbar');
      return;
    }

    try {
      const url = isUpdate
        ? `${API_BASE_URL}/api/calendar/templates/${templateData.id}`
        : `${API_BASE_URL}/api/calendar/templates`;

      const response = await fetch(url, {
        method: isUpdate ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(templateData)
      });

      if (response.ok) {
        const result = await response.json();

        if (isUpdate) {
          setTemplates(prev => prev.map(t => t.id === templateData.id ? result.template : t));
          console.log('✅ Template updated:', templateData.template_name);

          trackEvent('calendar_template_updated', {
            template_name: templateData.template_name,
            template_id: templateData.id
          });
        } else {
          setTemplates(prev => [...prev, result.template]);
          console.log('✅ Template created:', templateData.template_name);

          trackEvent('calendar_template_created', {
            template_name: templateData.template_name
          });
        }
      } else {
        throw new Error('Failed to save template');
      }
    } catch (error) {
      console.error('❌ Save template error:', error);
      setError('Template konnte nicht gespeichert werden');
    }
  }, [token, trackEvent]);

  const deleteTemplate = useCallback(async (templateId) => {
    if (!token) {
      setError('Kein Token verfügbar');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/calendar/templates/${templateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const deletedTemplate = templates.find(t => t.id === templateId);
        setTemplates(prev => prev.filter(t => t.id !== templateId));
        console.log('✅ Template deleted');

        trackEvent('calendar_template_deleted', {
          template_name: deletedTemplate?.template_name,
          template_id: templateId
        });
      } else {
        throw new Error('Failed to delete template');
      }
    } catch (error) {
      console.error('❌ Delete template error:', error);
      setError('Template konnte nicht gelöscht werden');
    }
  }, [token, templates, trackEvent]);

  useEffect(() => {
    if (!active) return;

    if (checkCacheValid() && GLOBAL_APPOINTMENTS_CACHE.length > 0) {
      console.log('📦 Using global cache on mount');
      setAppointments(GLOBAL_APPOINTMENTS_CACHE);
      setIsLoading(false);
      setIsInitialLoad(false);
      return;
    }

    if (isAuthLoading || tokenLoading) {
      console.log('⏳ Auth/Token loading...');
      return;
    }

    if (!isAuthenticated) {
      console.log('🔒 Not authenticated');
      setIsLoading(false);
      setIsInitialLoad(false);
      return;
    }

    if (!token) {
      console.log('⏳ Waiting for token from TokenContext...');
      return;
    }

    console.log('🎬 Starting initial load');
    loadAppointments();
  }, [active, isAuthenticated, isAuthLoading, tokenLoading, token, loadAppointments]);

  useEffect(() => {
    if (token && active) {
      loadTemplates();
    }
  }, [token, active, loadTemplates]);

  useEffect(() => {
    if (active && isAuthenticated && appointments.length > 0) {
      notificationService.startReminderChecking(appointments, 60000);
      return () => {
        notificationService.stopReminderChecking();
      };
    }
  }, [active, isAuthenticated, appointments]);

  const handleRetry = useCallback(() => {
    console.log('🔄 Manual retry');
    GLOBAL_HAS_LOADED = false;
    setCalendarCacheLoaded(false);
    GLOBAL_LOADING_PROMISE = null;
    setError(null);
    setIsInitialLoad(true);
    loadAppointments(true);
  }, [loadAppointments]);

  const handleSearch = useCallback(async (query) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    setSearchQuery(query);

    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      setIsSearching(false);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      if (!token) return;

      setIsSearching(true);
      setShowSearchResults(true);

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/calendar/search?q=${encodeURIComponent(query.trim())}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          console.log(`🔍 Search results: ${data.count} found for "${query}"`);
          setSearchResults(data.results || []);

          trackEvent('calendar_search', {
            query: query,
            results_count: data.count
          });
        } else {
          console.error('❌ Search failed:', response.status);
          setSearchResults([]);
        }
      } catch (err) {
        console.error('❌ Search error:', err);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
  }, [token, trackEvent]);

  const handleSearchResultClick = useCallback((appointment) => {
    setCurrentDate(new Date(appointment.date));
    setView('day');
    setShowSearchResults(false);
    setSearchQuery('');

    setEditingAppointment(appointment);
    setShowModal(true);

    trackEvent('calendar_search_result_click', {
      appointment_id: appointment.id,
      appointment_date: appointment.date
    });
  }, [trackEvent]);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
  }, []);

  const navigateDate = useCallback((direction) => {
    if (isAnimating || isLoading) return;
    
    setSwipeDirection(direction > 0 ? 'left' : 'right');
    setIsAnimating(true);
    
    setTimeout(() => {
      const newDate = new Date(currentDate);
      
      switch (view) {
        case 'day':
          newDate.setDate(newDate.getDate() + direction);
          break;
        case 'week':
          newDate.setDate(newDate.getDate() + (direction * 7));
          break;
        case 'month':
        case 'agenda':
          newDate.setMonth(newDate.getMonth() + direction);
          break;
        default:
          newDate.setDate(newDate.getDate() + direction);
          break;
      }
      
      setCurrentDate(newDate);
      
      trackEvent('calendar_navigate', {
        direction: direction > 0 ? 'next' : 'prev',
        view: view,
        targetDate: newDate.toISOString().split('T')[0]
      });
      
      setTimeout(() => {
        setSwipeDirection(null);
        setIsAnimating(false);
      }, 150);
    }, 150);
  }, [currentDate, view, isAnimating, isLoading, trackEvent]);

  const swipeHandlers = useSwipe({
    onSwipeLeft: () => navigateDate(1),
    onSwipeRight: () => navigateDate(-1),
    minSwipeDistance: 50,
    maxSwipeTime: 500
  });

  const handleViewChange = useCallback((newView) => {
    setView(newView);
    
    trackEvent('calendar_view_change', {
      from_view: view,
      to_view: newView
    });
  }, [view, trackEvent]);

  const handleToday = () => {
    setCurrentDate(new Date());
    
    trackEvent('calendar_navigate', {
      direction: 'today',
      view: view
    });
  };

  const handleCreateAppointment = (date = null, time = null) => {
    if (!isOnline) {
      notificationService.warning('Offline - Termine können nur angesehen werden');
      return;
    }

    const appointmentDate = date || currentDate;
    const appointmentTime = time || '09:00';

    setEditingAppointment({
      date: formatInputDate(appointmentDate),
      time: appointmentTime
    });
    setShowModal(true);
  };

  const handleEditAppointment = (appointment) => {
    if (!isOnline) {
      notificationService.warning('Offline - Termine können nur angesehen werden');
      return;
    }

    if (appointment.isRecurring && appointment.originalId) {
      const original = appointments.find(a => a.id === appointment.originalId && !a.isRecurring);
      setEditingAppointment(original || appointment);
    } else {
      setEditingAppointment(appointment);
    }
    setShowModal(true);
  };

  const handleSaveAppointment = useCallback(async (appointmentData) => {
    console.log('💾 Saving appointment:', appointmentData.title);

    if (!isOnline) {
      notificationService.warning('Offline - Termine können nicht gespeichert werden');
      return;
    }

    if (!token) {
      setError('Kein Token verfügbar');
      return;
    }

    const isEdit = !!(editingAppointment && editingAppointment.id);
    
    if (!isEdit) {
      const tempId = `temp-${Date.now()}`;
      const optimisticAppointment = {
        ...appointmentData,
        id: tempId
      };
      
      const addFn = (prev) => [...prev, optimisticAppointment];
      setAppointments(addFn);
      GLOBAL_APPOINTMENTS_CACHE = addFn(GLOBAL_APPOINTMENTS_CACHE);
      
      setShowModal(false);
      setEditingAppointment(null);
      
      try {
        const response = await calendarAPI.save(appointmentData, token);
        
        if (response.success || response.appointment || response.id) {
          const savedAppointment = response.appointment || response;
          console.log('✅ Appointment created:', savedAppointment.id);
          
          const replaceFn = (prev) => prev.map(a => 
            a.id === tempId ? { ...appointmentData, ...savedAppointment } : a
          );
          setAppointments(replaceFn);
          GLOBAL_APPOINTMENTS_CACHE = replaceFn(GLOBAL_APPOINTMENTS_CACHE);
          
          trackEvent('calendar_appointment_create', {
            title: appointmentData.title,
            date: appointmentData.date,
            hasReminder: !!appointmentData.reminder_minutes,
            isRecurring: !!appointmentData.recurrence_type
          });
          
          setTimeout(() => loadAppointments(true), 1000);
        } else {
          throw new Error(response.error || 'Create failed');
        }
        
        if (appointmentData.reminder_minutes) {
          await notificationService.scheduleReminder(appointmentData);
        }
        
      } catch (err) {
        console.error('❌ Create error:', err);
        
        const removeFn = (prev) => prev.filter(a => a.id !== tempId);
        setAppointments(removeFn);
        GLOBAL_APPOINTMENTS_CACHE = removeFn(GLOBAL_APPOINTMENTS_CACHE);
        
        setError(t('calendar.errorSaving') + ': ' + err.message);
      }
    } else {
      const oldAppointment = editingAppointment;
      const updatedAppointment = { ...oldAppointment, ...appointmentData };
      
      const updateFn = (prev) => prev.map(a => 
        a.id === oldAppointment.id ? updatedAppointment : a
      );
      setAppointments(updateFn);
      GLOBAL_APPOINTMENTS_CACHE = updateFn(GLOBAL_APPOINTMENTS_CACHE);
      
      setShowModal(false);
      setEditingAppointment(null);
      
      try {
        const response = await calendarAPI.update({ id: oldAppointment.id, ...appointmentData }, token);
        
        if (response.success || response.appointment || response.id) {
          console.log('✅ Appointment updated');
          
          trackEvent('calendar_appointment_edit', {
            appointment_id: oldAppointment.id,
            title: appointmentData.title,
            date: appointmentData.date
          });
          
          setTimeout(() => loadAppointments(true), 1000);
        } else {
          throw new Error(response.error || 'Update failed');
        }
        
        if (appointmentData.reminder_minutes) {
          await notificationService.scheduleReminder(appointmentData);
        }
        
      } catch (err) {
        console.error('❌ Update error:', err);
        
        const rollbackFn = (prev) => prev.map(a => 
          a.id === oldAppointment.id ? oldAppointment : a
        );
        setAppointments(rollbackFn);
        GLOBAL_APPOINTMENTS_CACHE = rollbackFn(GLOBAL_APPOINTMENTS_CACHE);
        
        setError(t('calendar.errorSaving') + ': ' + err.message);
      }
    }
  }, [editingAppointment, loadAppointments, t, trackEvent, token, isOnline]);

  const handleDeleteAppointment = useCallback(async (id) => {
    if (!isOnline) {
      notificationService.warning('Offline - Termine können nicht gelöscht werden');
      return;
    }

    if (!window.confirm(t('calendar.confirmDelete'))) return;

    console.log('🗑️ Deleting appointment:', id);

    if (!token) {
      setError('Kein Token verfügbar');
      return;
    }

    const deletedAppointment = appointments.find(a => a.id === id);
    const deletedIndex = appointments.findIndex(a => a.id === id);

    const removeFn = (prev) => prev.filter(a => a.id !== id && a.originalId !== id);
    setAppointments(removeFn);
    GLOBAL_APPOINTMENTS_CACHE = removeFn(GLOBAL_APPOINTMENTS_CACHE);

    setShowModal(false);
    setEditingAppointment(null);

    try {
      const response = await calendarAPI.delete({ id }, token);

      if (response.success) {
        console.log('✅ Appointment deleted');

        trackEvent('calendar_appointment_delete', {
          appointment_id: id,
          title: deletedAppointment?.title
        });
      }
    } catch (err) {
      console.error('❌ Delete error:', err);

      if (deletedAppointment) {
        const restoreFn = (prev) => {
          const newList = [...prev];
          newList.splice(deletedIndex, 0, deletedAppointment);
          return newList;
        };
        setAppointments(restoreFn);
        GLOBAL_APPOINTMENTS_CACHE = restoreFn(GLOBAL_APPOINTMENTS_CACHE);
      }

      setError(t('calendar.errorDeleting') + ': ' + err.message);
    }
  }, [appointments, t, trackEvent, token, isOnline]);

  const handleAppointmentDrop = useCallback(async (appointmentId, newDate, newTime) => {
    console.log('🎯 Dropping appointment:', appointmentId, 'to', newDate, newTime);

    if (!token) {
      setError('Kein Token verfügbar');
      return;
    }

    const appointment = appointments.find(a => a.id === parseInt(appointmentId) || a.id === appointmentId);
    if (!appointment) {
      console.error('❌ Appointment not found:', appointmentId);
      return;
    }

    if (appointment.isRecurring) {
      setError('Wiederholende Termine können nicht verschoben werden');
      return;
    }

    const oldDate = appointment.date;
    const oldTime = appointment.time;
    const updatedAppointment = {
      ...appointment,
      date: newDate,
      time: newTime
    };

    const updateFn = (prev) => prev.map(a =>
      (a.id === appointment.id) ? updatedAppointment : a
    );
    setAppointments(updateFn);
    GLOBAL_APPOINTMENTS_CACHE = updateFn(GLOBAL_APPOINTMENTS_CACHE);

    try {
      const response = await calendarAPI.update({
        id: appointment.id,
        ...updatedAppointment
      }, token);

      if (response.success || response.appointment) {
        console.log('✅ Appointment moved successfully');

        trackEvent('calendar_appointment_dragged', {
          appointment_id: appointment.id,
          from_date: oldDate,
          to_date: newDate,
          from_time: oldTime,
          to_time: newTime
        });
      } else {
        throw new Error(response.error || 'Move failed');
      }
    } catch (err) {
      console.error('❌ Move error:', err);

      const rollbackFn = (prev) => prev.map(a =>
        (a.id === appointment.id) ? appointment : a
      );
      setAppointments(rollbackFn);
      GLOBAL_APPOINTMENTS_CACHE = rollbackFn(GLOBAL_APPOINTMENTS_CACHE);

      setError('Termin konnte nicht verschoben werden: ' + err.message);
    }
  }, [appointments, token, trackEvent]);

  const handleExportAll = () => {
    if (appointments.length === 0) {
      alert(t('calendar.noAppointmentsToExport'));
      return;
    }
    const filename = `planivio-termine-${new Date().toISOString().split('T')[0]}.ics`;
    icsService.generateMultipleICS(appointments, filename);

    trackEvent('calendar_export', {
      format: 'ics',
      appointmentCount: appointments.length
    });
  };

  const handleExportPDF = useCallback(() => {
    console.log('📄 PDF Export triggered, appointments:', appointments.length);

    if (appointments.length === 0) {
      alert(t('calendar.noAppointmentsToExport'));
      return;
    }

    try {
      let filename;
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();

      if (view === 'month') {
        filename = exportMonthToPDF(year, month, appointments);
      } else if (view === 'week') {
        const weekStart = new Date(currentDate);
        const dayOfWeek = weekStart.getDay();
        const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        weekStart.setDate(weekStart.getDate() + diff);
        filename = exportWeekToPDF(weekStart, appointments);
      } else if (view === 'day') {
        filename = exportDayToPDF(currentDate, appointments);
      } else {
        filename = exportMonthToPDF(year, month, appointments);
      }

      trackEvent('calendar_export', {
        format: 'pdf',
        view: view,
        appointmentCount: appointments.length
      });

      console.log('📄 PDF Export successful:', filename);
    } catch (error) {
      console.error('❌ PDF Export failed:', error);
      alert('PDF-Export fehlgeschlagen: ' + error.message);
    }
  }, [appointments, view, currentDate, trackEvent, t]);

  const handleEnableNotifications = async () => {
    const granted = await notificationService.requestPermission();
    if (granted) {
      setShowNotificationBanner(false);
      alert('✅ ' + t('calendar.notificationsEnabled'));
      
      trackEvent('calendar_notification_enabled', {
        success: true
      });
    } else {
      alert('❌ ' + t('calendar.notificationsDenied'));
      
      trackEvent('calendar_notification_enabled', {
        success: false
      });
    }
  };

  const getSwipeAnimationClass = () => {
    if (!swipeDirection) return '';
    return swipeDirection === 'left' ? 'swipe-exit-left' : 'swipe-exit-right';
  };

  const handleImportClick = () => {
    setShowImportModal(true);
    setImportFile(null);
    setImportPreview([]);
    setImportError(null);
    trackEvent('calendar_import_modal_open');
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.ics')) {
      setImportError('Bitte wählen Sie eine .ics Datei aus');
      return;
    }

    setImportFile(file);
    setImportError(null);

    try {
      const parsedEvents = await icsService.readICSFile(file);
      const { valid, invalid, errors } = icsService.validateImport(parsedEvents);

      setImportPreview(valid);

      if (errors.length > 0) {
        setImportError(`${errors.length} Termine konnten nicht gelesen werden`);
      }

      console.log('📥 ICS Preview:', valid.length, 'valid,', invalid.length, 'invalid');
    } catch (err) {
      console.error('❌ ICS Parse error:', err);
      setImportError('Fehler beim Lesen der Datei: ' + err.message);
      setImportPreview([]);
    }
  };

  const handleImportConfirm = async () => {
    if (importPreview.length === 0) {
      setImportError('Keine Termine zum Importieren');
      return;
    }

    setIsImporting(true);
    setImportError(null);

    try {
      let successCount = 0;
      let failCount = 0;

      for (const apt of importPreview) {
        try {
          const response = await fetch(`${API_BASE_URL}/api/calendar`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              title: apt.title,
              description: apt.description || '',
              date: apt.date,
              time: apt.time || '00:00',
              end_time: apt.end_time || null,
              type: apt.category || 'privat',
              person: apt.person || 'alle',
              location: apt.location || '',
              recurring: apt.recurring || 'none',
              recurring_until: apt.recurring_until || null,
              reminder_minutes: apt.reminder_minutes || null,
              all_day: apt.all_day || false
            })
          });

          if (response.ok) {
            successCount++;
          } else {
            const errBody = await response.json().catch(() => ({}));
            console.warn('⚠️ Import failed for:', apt.title, response.status, errBody);
            failCount++;
          }
        } catch (importErr) {
          console.warn('⚠️ Import exception for:', apt.title, importErr.message);
          failCount++;
        }
      }

      await loadAppointments();

      trackEvent('calendar_import_complete', {
        success: successCount,
        failed: failCount,
        total: importPreview.length
      });

      setShowImportModal(false);
      alert(`✅ ${successCount} Termine importiert` + (failCount > 0 ? `, ${failCount} fehlgeschlagen` : ''));

    } catch (err) {
      console.error('❌ Import error:', err);
      setImportError('Import fehlgeschlagen: ' + err.message);
    } finally {
      setIsImporting(false);
    }
  };

  const handleImportCancel = () => {
    setShowImportModal(false);
    setImportFile(null);
    setImportPreview([]);
    setImportError(null);
  };

  // ============================================================================
  // HELPERS: Date Display
  // ============================================================================
  const getDatePrimary = () => {
    if (view === 'week') {
      const d = new Date(currentDate);
      d.setHours(0, 0, 0, 0);
      d.setDate(d.getDate() + 3 - (d.getDay() + 6) % 7);
      const week1 = new Date(d.getFullYear(), 0, 4);
      const weekNum = 1 + Math.round(((d - week1) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
      return `KW ${weekNum}`;
    }
    if (view === 'day') return currentDate.toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric' });
    if (view === 'month') return currentDate.toLocaleDateString('de-DE', { month: 'long' });
    if (view === 'agenda') return currentDate.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
    return '';
  };

  const getDateSecondary = () => {
    if (view === 'week') {
      const weekStart = new Date(currentDate);
      const dayOfWeek = weekStart.getDay();
      const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      weekStart.setDate(weekStart.getDate() + diff);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      return `${weekStart.getDate()}.${weekStart.getMonth() + 1}. - ${weekEnd.getDate()}.${weekEnd.getMonth() + 1}.${weekEnd.getFullYear()}`;
    }
    if (view === 'day') return currentDate.toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' });
    if (view === 'month') return String(currentDate.getFullYear());
    return '';
  };

  // ============================================================================
  // RENDER: Not Active
  // ============================================================================
  if (!active) return null;

  // ============================================================================
  // RENDER: Not Authenticated
  // ============================================================================
  if (!isAuthenticated && !isAuthLoading) {
    return (
      <div className={rootClassName}>
        <div className="cal-auth-required">
          <span className="cal-auth-icon">🔒</span>
          <p>{t('calendar.pleaseLogin')}</p>
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER: SKELETON UI
  // ============================================================================
  if ((isInitialLoad || tokenLoading) && appointments.length === 0 && !error) {
    return (
      <div className={rootClassName}>
        <div className="cal-skeleton-header">
          <Skeleton width={48} height={48} borderRadius={12} />
          <div>
            <Skeleton width={100} height={24} style={{ marginBottom: 8 }} />
            <Skeleton width={80} height={16} />
          </div>
        </div>
        
        <div className="cal-skeleton-toolbar">
          <Skeleton width={300} height={44} borderRadius={12} />
          <Skeleton width={100} height={44} borderRadius={10} />
          <Skeleton width={100} height={44} borderRadius={10} />
          <Skeleton width={100} height={44} borderRadius={10} />
        </div>
        
        <div className="cal-skeleton-nav">
          <Skeleton width={40} height={40} borderRadius={10} />
          <Skeleton width={100} height={40} borderRadius={10} />
          <Skeleton width={40} height={40} borderRadius={10} />
        </div>
        
        <Skeleton width="100%" height={400} borderRadius={16} />
      </div>
    );
  }

  // ============================================================================
  // RENDER: Auth Loading
  // ============================================================================
  if (isAuthLoading) {
    return (
      <div className={rootClassName}>
        <Loading icon="🔐" text="Authentifizierung..." />
      </div>
    );
  }

  // ============================================================================
  // RENDER: Error
  // ============================================================================
  if (error && appointments.length === 0) {
    return (
      <div className={rootClassName}>
        <div className="cal-error-state">
          <span className="cal-error-icon">⚠️</span>
          <p className="cal-error-message">{error}</p>
          <button className="cal-retry-btn" onClick={handleRetry}>
            🔄 {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER: MAIN
  // ============================================================================
  return (
    <div className={rootClassName}>

      {showLoading && (
        <LoadingIndicator text="Aktualisiere..." position="fixed" />
      )}

      {/* OFFLINE BANNER */}
      {!isOnline && (
        <div className="cal-banner cal-banner--offline">
          <div className="cal-banner-content">
            <span className="cal-banner-icon">📴</span>
            <strong className="cal-banner-title">Offline-Modus</strong>
            <span className="cal-banner-desc">
              Nur Ansicht - Termine können nicht bearbeitet werden
            </span>
          </div>
        </div>
      )}

      {/* NOTIFICATION BANNER */}
      {showNotificationBanner && (
        <div className="cal-banner cal-banner--notification">
          <div className="cal-banner-content">
            <strong className="cal-banner-title">
              <span className="cal-banner-icon">🔔</span>
              {t('calendar.enableNotifications')}
            </strong>
            <p className="cal-banner-desc">
              {t('calendar.notificationHint')}
            </p>
          </div>
          <div className="cal-banner-actions">
            <button className="cal-banner-btn cal-banner-btn--primary" onClick={handleEnableNotifications}>
              {t('calendar.activate')}
            </button>
            <button className="cal-banner-btn cal-banner-btn--secondary" onClick={() => setShowNotificationBanner(false)}>
              {t('calendar.later')}
            </button>
          </div>
        </div>
      )}

      {/* REMINDER BANNER */}
      {showReminderBanner && reminderPermission === 'default' && (
        <div className="cal-banner cal-banner--reminder">
          <div className="cal-banner-content">
            <strong className="cal-banner-title">
              <span className="cal-banner-icon">🔔</span>
              Erinnerungen aktivieren?
            </strong>
            <p className="cal-banner-desc">
              Erhalte Browser-Benachrichtigungen vor deinen Terminen.
            </p>
          </div>
          <div className="cal-banner-actions">
            <button className="cal-banner-btn cal-banner-btn--primary" onClick={handleRequestReminderPermission}>
              Aktivieren
            </button>
            <button className="cal-banner-btn cal-banner-btn--secondary" onClick={handleDismissReminderBanner}>
              Später
            </button>
          </div>
        </div>
      )}

      {/* INLINE ERROR (with appointments still visible) */}
      {error && appointments.length > 0 && (
        <div className="cal-inline-error">
          <span>⚠️</span>
          {error}
          <button className="cal-inline-error-close" onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* BODY WRAPPER */}
      <div className={`cal-body ${showLoading ? 'cal-body--loading' : ''}`}>
        
        {/* ===== ROW 1: Icon + Title + Info + Hamburger ===== */}
        <div className="cal-header-row1">
          <div className="cal-header-left">
            <div className="cal-header-icon">📅</div>
            <div className="cal-header-title-wrap">
              <h1 className="cal-header-title">{t('calendar.title')}</h1>
              <span className="cal-header-subtitle">{appointments.length} Termine</span>
            </div>
            <button
              className="cal-info-btn"
              onClick={() => setShowInfo(!showInfo)}
              title="Modul-Info"
            >
              ⓘ
            </button>
          </div>

          {/* Hamburger Menu */}
          <div className="cal-hamburger-wrapper" ref={hamburgerRef}>
            <button
              className="cal-hamburger-btn"
              onClick={(e) => {
                e.stopPropagation();
                setShowMobileExportMenu(!showMobileExportMenu);
              }}
            >
              ☰
            </button>

            {showMobileExportMenu && (
              <div className="cal-hamburger-dropdown" style={hamburgerRef.current ? { top: hamburgerRef.current.getBoundingClientRect().bottom + 8 + 'px' } : {}}>

                {/* Search - nur Desktop */}
                {!isMobile && (
                  <div className="cal-hamburger-search">
                    <span className="cal-search-icon">🔍</span>
                    <input
                      type="text"
                      className="cal-search-input"
                      placeholder="Termine suchen..."
                      value={searchQuery}
                      onChange={(e) => handleSearch(e.target.value)}
                    />
                    {searchQuery && (
                      <button className="cal-search-clear" onClick={clearSearch}>✕</button>
                    )}
                  </div>
                )}

                {!isMobile && showSearchResults && searchResults.length > 0 && (
                  <div className="cal-hamburger-search-results">
                    {searchResults.map(apt => (
                      <div
                        key={apt.id}
                        className="cal-search-result-item"
                        onClick={() => { handleSearchResultClick(apt); setShowMobileExportMenu(false); }}
                      >
                        <div className="cal-search-result-title">{apt.title}</div>
                        <div className="cal-search-result-meta">
                          📅 {new Date(apt.date).toLocaleDateString('de-DE')}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="cal-hamburger-divider" />

                <button
                  className="cal-hamburger-item"
                  onClick={() => { handleExportAll(); setShowMobileExportMenu(false); }}
                  disabled={isLoading || appointments.length === 0}
                >
                  <span>📤</span>
                  <span>ICS Export</span>
                </button>

                <button
                  className="cal-hamburger-item"
                  onClick={() => { handleExportPDF(); setShowMobileExportMenu(false); }}
                  disabled={isLoading || appointments.length === 0}
                >
                  <span>📄</span>
                  <span>PDF Export</span>
                </button>

                <button
                  className="cal-hamburger-item"
                  onClick={() => { handleImportClick(); setShowMobileExportMenu(false); }}
                  disabled={isLoading}
                >
                  <span>📥</span>
                  <span>ICS Import</span>
                </button>

                {/* Template Selector - nur Desktop */}
                {!isMobile && (
                  <>
                    <div className="cal-hamburger-divider" />
                    <button
                      className="cal-hamburger-item"
                      onClick={() => { setShowTemplateManager(true); setShowMobileExportMenu(false); }}
                    >
                      <span>📋</span>
                      <span>Templates verwalten</span>
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Info Modal */}
        {showInfo && (
          <div className="cal-info-modal">
            <div className="cal-info-modal-header">
              <span className="cal-info-modal-title">📅 Kalender - Info</span>
              <button className="cal-info-close-btn" onClick={() => setShowInfo(false)}>✕</button>
            </div>
            <div className="cal-info-modal-content">
              <p className="cal-info-text">
                <strong>Kalender-Modul</strong> hilft dir bei der Verwaltung aller Termine
                deiner Familie. Plane Arzttermine, Schulveranstaltungen, Geburtstage
                und mehr an einem zentralen Ort.
              </p>
              <ul className="cal-info-list">
                <li>Erstelle einmalige oder wiederkehrende Termine</li>
                <li>Kategorisiere Termine (Arbeit, Schule, Arzt, etc.)</li>
                <li>Verschiedene Ansichten: Tag, Woche, Monat, Agenda</li>
                <li>Export als PDF oder ICS für andere Kalender</li>
                <li>Importiere ICS-Dateien von anderen Apps</li>
                <li>Erinnerungen per Browser-Benachrichtigung</li>
              </ul>
            </div>
          </div>
        )}

        {/* ===== ROW 2: Kategorie Filter ===== */}
        {/* ✅ v35.4: FAB aus Row2 entfernt — jetzt nur noch Kategorie */}
        <div className="cal-header-row2-controls">
          {/* Category Filter */}
          <div className="cal-filter-wrapper">
            <button
              className={`cal-filter-btn ${selectedCategories.length > 0 ? 'cal-filter-btn--active' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                setShowCategoryFilter(!showCategoryFilter);
              }}
            >
              <span>🏷️</span>
              <span>Kategorie</span>
              {selectedCategories.length > 0 && (
                <span className="cal-filter-badge">{selectedCategories.length}</span>
              )}
            </button>

            {showCategoryFilter && (
              <div className="cal-filter-dropdown">
                <div className="cal-filter-dropdown-header">
                  <button className="cal-filter-dropdown-link" onClick={clearCategoryFilter}>
                    Alle anzeigen
                  </button>
                  <button className="cal-filter-dropdown-link" onClick={selectAllCategories}>
                    Alle auswählen
                  </button>
                </div>

                {CATEGORIES.map(cat => (
                  <label
                    key={cat.value}
                    className={`cal-filter-item ${selectedCategories.includes(cat.value) ? 'cal-filter-item--selected' : ''}`}
                    style={{ background: selectedCategories.includes(cat.value) ? `${cat.color}15` : 'transparent' }}
                  >
                    <input
                      type="checkbox"
                      className="cal-filter-checkbox"
                      checked={selectedCategories.includes(cat.value)}
                      onChange={() => toggleCategoryFilter(cat.value)}
                      style={{ accentColor: cat.color }}
                    />
                    <span className="cal-filter-item-icon">{cat.icon}</span>
                    <span className="cal-filter-item-label">{cat.label}</span>
                    <span className="cal-filter-dot" style={{ background: cat.color }} />
                  </label>
                ))}

                <div className="cal-filter-footer">
                  <button className="cal-filter-done-btn" onClick={() => setShowCategoryFilter(false)}>
                    Fertig
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ===== ROW 3: ◁ ▷ | Heute | View Selector ===== */}
        <div className="cal-header-row2">
          {/* Navigation Arrows */}
          <div className="cal-nav-arrows">
            <button
              className="cal-nav-arrow"
              onClick={() => navigateDate(-1)}
              disabled={isLoading || isAnimating}
            >◁</button>
            <button
              className="cal-nav-arrow"
              onClick={() => navigateDate(1)}
              disabled={isLoading || isAnimating}
            >▷</button>
          </div>

          <button
            className="cal-today-btn"
            onClick={handleToday}
            disabled={isLoading}
          >
            {t('calendar.today')}
          </button>

          <div className="cal-view-selector">
            {VIEW_CONFIG.map(v => (
              <button
                key={v.id}
                className={`cal-view-btn ${view === v.id ? 'active' : ''}`}
                onClick={() => handleViewChange(v.id)}
                disabled={isLoading}
              >
                <span>{v.icon}</span>
                <span>{v.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* ===== VIEW CONTENT ===== */}
        <div
          className={`cal-content ${getSwipeAnimationClass()}`}
          {...swipeHandlers}
        >
          {selectedCategories.length > 0 && (
            <div className="cal-filter-active">
              <span>🏷️</span>
              <span>
                Filter aktiv: {filteredAppointments.length} von {appointments.length} Terminen
              </span>
              <button className="cal-filter-active-clear" onClick={clearCategoryFilter}>
                Filter entfernen
              </button>
            </div>
          )}

          {view === 'day' && (
            <DayView
              currentDate={currentDate}
              appointments={filteredAppointments}
              onAppointmentClick={handleEditAppointment}
              onAppointmentDrop={handleAppointmentDrop}
              onTimeSlotClick={handleCreateAppointment}
            />
          )}
          {view === 'week' && (
            <WeekView
              currentDate={currentDate}
              appointments={filteredAppointments}
              onAppointmentClick={handleEditAppointment}
              onAppointmentDrop={handleAppointmentDrop}
              onTimeSlotClick={handleCreateAppointment}
            />
          )}
          {view === 'month' && (
            <MonthView currentDate={currentDate} appointments={filteredAppointments} onAppointmentClick={handleEditAppointment} />
          )}
          {view === 'agenda' && (
            <AgendaView currentDate={currentDate} appointments={filteredAppointments} onAppointmentClick={handleEditAppointment} />
          )}
        </div>
      </div>
      {/* ═══ Ende cal-body ═══ */}

      {/* Modal */}
      <AppointmentModal
        show={showModal}
        onClose={() => { setShowModal(false); setEditingAppointment(null); }}
        onSave={handleSaveAppointment}
        onDelete={handleDeleteAppointment}
        appointment={editingAppointment}
        defaultDate={formatInputDate(currentDate)}
      />

      {/* Template Manager Modal */}
      {showTemplateManager && (
        <TemplateManager
          templates={templates}
          onClose={() => setShowTemplateManager(false)}
          onSave={saveTemplate}
          onDelete={deleteTemplate}
        />
      )}

      {/* ICS Import Modal */}
      {showImportModal && (
        <div className="cal-modal-overlay" onClick={handleImportCancel}>
          <div className="cal-modal" onClick={(e) => e.stopPropagation()}>

            <div className="cal-modal-header">
              <h2 className="cal-modal-title">📥 ICS Importieren</h2>
              <button className="cal-modal-close" onClick={handleImportCancel}>×</button>
            </div>

            <div className="cal-modal-body">
              <div style={{ marginBottom: '1.5rem' }}>
                <label className="cal-modal-label">ICS-Datei auswählen</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".ics"
                  onChange={handleFileSelect}
                  className="cal-file-input"
                />
                <p className="cal-modal-hint">
                  Unterstützt: Google Calendar, Outlook, Apple Calendar
                </p>
              </div>

              {importError && (
                <div className="cal-import-error">
                  ⚠️ {importError}
                </div>
              )}

              {importPreview.length > 0 && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <h3 className="cal-import-preview-title">
                    Vorschau ({importPreview.length} Termine)
                  </h3>
                  <div className="cal-import-preview-list">
                    {importPreview.slice(0, 10).map((apt, idx) => (
                      <div key={idx} className="cal-import-preview-item">
                        <div className="cal-import-preview-item-title">{apt.title}</div>
                        <div className="cal-import-preview-item-meta">
                          📅 {apt.date} {apt.time !== '00:00' && `🕐 ${apt.time}`}
                        </div>
                      </div>
                    ))}
                    {importPreview.length > 10 && (
                      <div className="cal-import-preview-more">
                        ... und {importPreview.length - 10} weitere Termine
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="cal-modal-footer">
                <button className="cal-modal-btn cal-modal-btn--cancel" onClick={handleImportCancel}>
                  Abbrechen
                </button>
                <button
                  className="cal-modal-btn cal-modal-btn--confirm"
                  onClick={handleImportConfirm}
                  disabled={importPreview.length === 0 || isImporting}
                >
                  {isImporting ? (
                    <>
                      <span style={{ animation: 'cal-spin 1s linear infinite' }}>⏳</span>
                      Importiere...
                    </>
                  ) : (
                    <>
                      📥 {importPreview.length} Termine importieren
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Calendar;